import { useState, useEffect } from 'react';
import { Heart, Users, Sparkles, Award } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Newsletter from '../components/Newsletter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_pooch-crew/artifacts/401fwdfd_Logo%202-main.jpg";

const DEFAULT_IMAGES = [
  'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=400',
  'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400',
  'https://images.unsplash.com/photo-1544568100-847a948585b9?w=400',
];

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return url;
};

const VALUES = [
  {
    icon: Heart,
    title: 'Love First',
    description: 'Every stitch is made with the same love you have for your furry friend.',
    color: '#e76f51',
  },
  {
    icon: Sparkles,
    title: 'Quality Always',
    description: 'Premium materials that feel as good as they look, for both of you.',
    color: '#c68b4d',
  },
  {
    icon: Users,
    title: 'Community Driven',
    description: 'Built by pet lovers, for pet lovers. Your feedback shapes our products.',
    color: '#1e3a5f',
  },
  {
    icon: Award,
    title: 'Sustainability',
    description: 'Eco-conscious materials and practices because we care about the planet.',
    color: '#22c55e',
  },
];

export const AboutPage = () => {
  const [images, setImages] = useState(DEFAULT_IMAGES);
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        const settings = response.data;
        
        if (settings?.logo_image) {
          setLogoUrl(getImageUrl(settings.logo_image));
        }
        
        const newImages = [...DEFAULT_IMAGES];
        if (settings?.about_image_1) newImages[0] = getImageUrl(settings.about_image_1);
        if (settings?.about_image_2) newImages[1] = getImageUrl(settings.about_image_2);
        if (settings?.about_image_3) newImages[2] = getImageUrl(settings.about_image_3);
        
        setImages(newImages);
      } catch (error) {
        console.log('Using default about images');
      }
    };
    fetchSettings();
  }, []);

  return (
    <div className="min-h-screen bg-[#f5f0e6]">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#f5f0e6]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-accent text-2xl text-[#c68b4d] block mb-4">Our Story</span>
              <h1
                data-testid="about-title"
                className="font-heading text-4xl md:text-6xl font-bold text-[#1e3a5f] mb-6 leading-tight"
              >
                More Than Just Matching Outfits
              </h1>
              <p className="font-body text-lg leading-relaxed text-[#1e3a5f]/70 mb-6">
                PoochCrew started with a simple idea: why should matching outfits be reserved for just humans? 
                Our founder, a devoted dog mom, noticed that the bond between pets and their owners deserved 
                to be celebrated in style.
              </p>
              <p className="font-body text-lg leading-relaxed text-[#1e3a5f]/70">
                Today, we create premium quality apparel that lets you and your best friend show the world 
                just how strong your bond is. Every piece is designed with comfort, style, and durability in mind.
              </p>
            </div>

            {/* Image Collage */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src={images[0]}
                      alt="Happy dog"
                      className="w-full h-48 object-cover"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src={images[1]}
                      alt="Dog in nature"
                      className="w-full h-64 object-cover"
                    />
                  </div>
                </div>
                <div className="pt-8">
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src={images[2]}
                      alt="Playful dog"
                      className="w-full h-80 object-cover"
                    />
                  </div>
                </div>
              </div>
              
              {/* Floating Logo */}
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-white rounded-full p-4 shadow-xl">
                <img
                  src={logoUrl}
                  alt="PoochCrew Logo"
                  className="w-20 h-20 rounded-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <span className="font-accent text-2xl text-[#c68b4d] block mb-2">What We Stand For</span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#1e3a5f]">
              Our Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {VALUES.map((value, index) => (
              <div
                key={value.title}
                data-testid={`value-card-${index}`}
                className="group bg-[#f5f0e6]/50 p-8 rounded-3xl hover:bg-white hover:shadow-lg transition-all duration-300"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${value.color}15` }}
                >
                  <value.icon className="w-7 h-7" style={{ color: value.color }} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-3">
                  {value.title}
                </h3>
                <p className="font-body text-base text-[#1e3a5f]/70 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 bg-[#1e3a5f]">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <span className="font-accent text-3xl text-[#c68b4d] block mb-4">Our Mission</span>
          <blockquote
            data-testid="mission-statement"
            className="font-heading text-3xl md:text-4xl font-medium text-white leading-relaxed mb-8"
          >
            "To celebrate the unbreakable bond between pets and their humans through 
            thoughtfully designed, high-quality matching apparel that brings joy to every adventure."
          </blockquote>
          <div className="flex items-center justify-center gap-4">
            <div className="w-12 h-1 bg-[#c68b4d] rounded-full" />
            <span className="font-body text-white/70">Est. 2022</span>
            <div className="w-12 h-1 bg-[#c68b4d] rounded-full" />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 bg-[#f5f0e6]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div data-testid="about-stat-1">
              <p className="font-heading text-5xl md:text-6xl font-bold text-[#1e3a5f] mb-2">10K+</p>
              <p className="font-body text-base text-[#1e3a5f]/70">Happy Pet Parents</p>
            </div>
            <div data-testid="about-stat-2">
              <p className="font-heading text-5xl md:text-6xl font-bold text-[#c68b4d] mb-2">50+</p>
              <p className="font-body text-base text-[#1e3a5f]/70">Unique Designs</p>
            </div>
            <div data-testid="about-stat-3">
              <p className="font-heading text-5xl md:text-6xl font-bold text-[#e76f51] mb-2">15</p>
              <p className="font-body text-base text-[#1e3a5f]/70">Countries Shipped</p>
            </div>
            <div data-testid="about-stat-4">
              <p className="font-heading text-5xl md:text-6xl font-bold text-[#1e3a5f] mb-2">4.9</p>
              <p className="font-body text-base text-[#1e3a5f]/70">Average Rating</p>
            </div>
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
};

export default AboutPage;
